import { useEffect, useState } from "react";
import { useOffline } from "../../offline/OfflineContext";
import type { RoleSnapshot } from "../../offline/model";
import { selectHasEmployment, usableRoleSnapshot } from "./roleSnapshot";
import { useOwnerKey } from "./tenantSelection";
import { useMe } from "./useMe";

/** Snapshot read for an owner. */
interface StoredSnapshot {
  /** Owner key the snapshot was read for. */
  readonly owner: string;
  /** The usable snapshot, or `null`. */
  readonly snapshot: RoleSnapshot | null;
}

/**
 * Returns whether the signed-in person has an employment in the chosen tenant (F-75): from
 * `GET /me`, or after an app start without connection from the last profile stored on this device
 * ({@link selectHasEmployment}). Like the roles of the navigation it only shows or hides a
 * function; the server decides every request on its own.
 *
 * @returns Whether an employment exists, or `undefined` while it is not known.
 */
export function useHasEmployment(): boolean | undefined {
  const me = useMe();
  const owner = useOwnerKey();
  const { store, nowMs } = useOffline();
  const [stored, setStored] = useState<StoredSnapshot | null>(null);

  useEffect(() => {
    if (owner === null) {
      return undefined;
    }

    let active = true;
    const apply = (snapshot: RoleSnapshot | null): void => {
      if (active) {
        setStored({ owner, snapshot: usableRoleSnapshot(snapshot, owner, nowMs()) });
      }
    };
    store.getRoleSnapshot(owner).then(apply, () => {
      apply(null);
    });
    const cleanup = (): void => {
      active = false;
    };

    return cleanup;
  }, [store, nowMs, owner]);

  const snapshot = stored !== null && stored.owner === owner ? stored.snapshot : null;
  const hasEmployment = selectHasEmployment({ profile: me.data, error: me.error, snapshot });

  return hasEmployment;
}
